import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Aquí iría la lógica para enviar el ticket o correo
    console.log('Form data submitted:', formData);
    setSnackbarOpen(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const faqs = [
    {
      question: '¿Cuáles son los tiempos de entrega?',
      answer: 'Nuestras entregas estándar se realizan en un plazo de 24 a 48 horas. Para envíos express, ofrecemos entrega el mismo día si el pedido se realiza antes de las 12:00 PM.'
    },
    {
      question: '¿Puedo personalizar mi arreglo floral?',
      answer: '¡Sí! Puedes elegir las flores, colores y el estilo de tu arreglo. Contáctanos por WhatsApp para una atención personalizada.'
    },
    {
      question: '¿Qué métodos de pago aceptan?',
      answer: 'Aceptamos tarjetas de crédito/débito (Visa, Mastercard, AMEX), transferencias bancarias y pagos en efectivo en tiendas participantes.'
    },
    {
      question: '¿Cómo puedo rastrear mi pedido?',
      answer: 'Una vez confirmado tu pedido, recibirás un correo electrónico con un enlace de seguimiento. También puedes ver el estado en la sección "Mis Pedidos" de tu perfil.'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography 
        variant="h3" 
        component="h1" 
        gutterBottom 
        align="center" 
        sx={{ 
          color: '#1a1a1a', 
          mb: 6,
          fontFamily: '"Playfair Display", serif',
          fontWeight: 700
        }}
      >
        Centro de Soporte
      </Typography>

      <Grid container spacing={4}>
        {/* Información de Contacto */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              height: '100%',
              borderRadius: 4,
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
              Contáctanos
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(76, 175, 80, 0.1)', 
                  mr: 2,
                  display: 'flex'
                }}
              >
                <WhatsAppIcon color="success" sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>WhatsApp</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>+52 222 222 2222</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(233, 30, 99, 0.1)', 
                  mr: 2,
                  display: 'flex'
                }}
              >
                <PhoneIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Teléfono</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>+52 222 222 2222</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(233, 30, 99, 0.1)', 
                  mr: 2,
                  display: 'flex'
                }}
              >
                <EmailIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Correo Electrónico</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>soporte@blum-art.mx</Typography>
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.8 }}>
              <strong>Horario de Atención:</strong><br />
              Lunes a Domingo<br />
              9:00 AM - 8:00 PM
            </Typography>
          </Paper>
        </Grid>

        {/* Formulario de Contacto */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4,
              borderRadius: 4,
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
              Envíanos un mensaje
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Correo Electrónico"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Asunto"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mensaje"
                    name="message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{ 
                      mt: 2, 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)',
                      py: 1.5
                    }}
                  >
                    Enviar Mensaje
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Preguntas Frecuentes */}
        <Grid item xs={12} sx={{ mt: 6 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            align="center" 
            sx={{ 
              mb: 4, 
              fontFamily: '"Playfair Display", serif', 
              fontWeight: 700 
            }}
          >
            Preguntas Frecuentes
          </Typography>
          {faqs.map((faq, index) => (
            <Accordion 
              key={index}
              elevation={0}
              sx={{ 
                mb: 2, 
                borderRadius: '16px !important',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                '&:before': { display: 'none' },
                border: '1px solid rgba(0,0,0,0.05)'
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: '"Playfair Display", serif' }}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: '#555', lineHeight: 1.6 }}>{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      </Grid>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
        >
          ¡Mensaje enviado! Nos pondremos en contacto contigo pronto.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SupportPage;
